import React from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  ArrowRight02Icon,
  ArrowLeft02Icon,
  ArrowDown01Icon,
  Call02Icon,
  Calendar03Icon,
  Location01Icon,
  CheckmarkCircle02Icon,
  Tick01Icon,
  Clock01Icon,
  StarIcon,
  User03Icon,
  Mail01Icon,
  Menu01Icon,
  SafeIcon,
  CaduceusIcon,
  InjectionIcon,
  Facebook01Icon,
  InstagramIcon,
  WhatsappIcon,
  Add01Icon,
  Remove01Icon
} from '@hugeicons/core-free-icons';

// Icon tokens for consistent sizing and styling
export const ICON_SIZES = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  xxl: 42
} as const;

type IconSize = typeof ICON_SIZES[keyof typeof ICON_SIZES];

interface IconProps {
  size?: IconSize;
  className?: string;
}

// SVG-safe wrapper for HugeiconsIcon to prevent rem unit errors
interface SafeIconWrapperProps {
  icon: any;
  size: IconSize;
  className?: string;
}

const SafeIconWrapper: React.FC<SafeIconWrapperProps> = ({ icon, size, className = "" }) => (
  <HugeiconsIcon 
    icon={icon} 
    size={size} 
    className={className}
    style={{ 
      width: `${size}px`, 
      height: `${size}px` 
    }}
  />
);

// Primary Icons (as requested)
export const Location: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Location01Icon} 
    size={size} 
    className={className}
  />
);

export const Phone: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Call02Icon} 
    size={size} 
    className={className}
  />
);

export const Calendar: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Calendar03Icon} 
    size={size} 
    className={className}
  />
);

export const ArrowRight: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={ArrowRight02Icon} 
    size={size} 
    className={className}
  />
);

export const ArrowLeft: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={ArrowLeft02Icon} 
    size={size} 
    className={className}
  />
);

export const ArrowDown: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={ArrowDown01Icon} 
    size={size} 
    className={className}
  />
);

export const CircleCheck: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={CheckmarkCircle02Icon} 
    size={size} 
    className={className}
  />
);

export const TickIcon: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Tick01Icon} 
    size={size} 
    className={className}
  />
);

// Additional Icons (keeping existing ones for compatibility)
export const Clock: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Clock01Icon} 
    size={size} 
    className={className}
  />
);

export const Star: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={StarIcon} 
    size={size} 
    className={className}
  />
);

export const User: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={User03Icon} 
    size={size} 
    className={className}
  />
);

export const Mail: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Mail01Icon} 
    size={size} 
    className={className}
  />
);

export const Menu: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Menu01Icon} 
    size={size} 
    className={className}
  />
);

// Medical Icons
export const Safe: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={SafeIcon} 
    size={size} 
    className={className}
  />
);

export const Caduceus: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={CaduceusIcon} 
    size={size} 
    className={className}
  />
);

export const Injection: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={InjectionIcon} 
    size={size} 
    className={className}
  />
);

// Social Media Icons
export const Facebook: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Facebook01Icon} 
    size={size} 
    className={className}
  />
);

export const Instagram: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={InstagramIcon} 
    size={size} 
    className={className}
  />
);

export const Whatsapp: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={WhatsappIcon} 
    size={size} 
    className={className}
  />
);

export const Add: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Add01Icon} 
    size={size} 
    className={className}
  />
);

export const Remove: React.FC<IconProps> = ({ size = ICON_SIZES.sm, className = "" }) => (
  <SafeIconWrapper 
    icon={Remove01Icon} 
    size={size} 
    className={className}
  />
);

// Legacy aliases for backward compatibility
export const MapPin = Location;
export const Check = CircleCheck; 